<template>
  <Page>
    <MobileViewBanner v-if="showControlRow" @visible="bannerVisible = $event" />
    <div
      class="flex flex-col gap-4 w-full"
      :class="{
        '[--schedule-sticky-offset:theme(spacing.14)]': bannerVisible
      }"
    >
      <template v-if="showControlRow && activeResource">
        <!-- The drawn columns, not the raw selection: the row says "side by
             side" about the grid below it, so it has to name the same
             schedules the grid does, fallback included. -->
        <MultiTechnicianControlRow
          v-if="phoneColumns.length > 1"
          :resources="phoneColumns"
          :date="selectedDate"
          @select="onControlRowSelect"
          @settings="onSettingsTap"
        />
        <UnassignedControlRow
          v-else-if="isUnassignedActive"
          :appointment-count="activeAppointmentCount"
          :date="selectedDate"
          :week-view="isWeekView"
          @select="onControlRowSelect"
          @settings="onSettingsTap"
        />
        <TechnicianControlRow
          v-else
          :resource="activeResource"
          :appointment-count="activeAppointmentCount"
          :date="selectedDate"
          :week-view="isWeekView"
          @select="onControlRowSelect"
          @settings="onSettingsTap"
        />
      </template>
      <ScheduleDateRow
        v-if="showControlRow"
        :date="selectedDate"
        :technician-count="technicianCount"
        @update-selected-date="handleDateChange"
        @return-to-today="onReturnToToday"
        @stepped="onDateStepped"
      />
      <ScheduleHeader
        :date="selectedDate"
        :view="view"
        :compact="showControlRow"
        @update-selected-date="handleDateChange"
        @update-view="handleViewChange"
      />
      <!-- The phone board butts against the date row above it: the column's
           `gap-4` would otherwise leave 16px between that row's bottom rule and
           the board's own, reading as two lines with a band between them. -->
      <div v-if="view === 'timeline'" :class="{ '-mt-4': showControlRow }">
        <MobileTimeline
          v-if="mobileView"
          :date="selectedDate"
          @date-navigated="handleDateChange"
        />
        <Timeline
          v-else
          :date="selectedDate"
          @date-navigated="handleDateChange"
        />
      </div>
      <div v-else>Capacity View</div>
    </div>
    <TechnicianPickerSheet
      v-if="pickerOpen"
      :resources="resources"
      :departments="departments"
      :selected-ids="sheetSelectedIds"
      :unassigned-count="unassignedCount"
      @apply="applySelection"
      @close="pickerOpen = false"
    />
    <ScheduleSettings v-if="settingsOpen" @close="settingsOpen = false" />
  </Page>
</template>

<script setup lang="ts">
import moment from "moment-timezone";
import { computed, onBeforeMount, ref, watch } from "vue";
import { useStore } from "vuex";
import { trackMobileWebEvent } from "@/lib/pendo";
import { syncStartParam } from "@/lib/scheduleStartParam";
import { getQueryVariables, updateQueryParam } from "@/lib/url";
import { useResolvedFeatureFlag } from "@/lib/useFeatureFlag";

import Page from "@/components/Page.vue";
import { useBreakpoints } from "@/composables/useBreakpoints";
import { useEventCalendar } from "@/SchedulerV2/useEventCalendar";
import { UNASSIGNED_RESOURCE_ID } from "@/typings/scheduler";
import {
  appointmentCountFor,
  isUnassignedAppointment
} from "./appointmentCounts";
import MobileTimeline from "./MobileTimeline.vue";
import MobileViewBanner from "./MobileViewBanner.vue";
import MultiTechnicianControlRow from "./MultiTechnicianControlRow.vue";
import ScheduleDateRow from "./ScheduleDateRow.vue";
import ScheduleHeader from "./ScheduleHeader.vue";
import ScheduleSettings from "./ScheduleSettings.vue";
import TechnicianControlRow from "./TechnicianControlRow.vue";
import TechnicianPickerSheet from "./TechnicianPickerSheet.vue";
import Timeline from "./Timeline.vue";
import UnassignedControlRow from "./UnassignedControlRow.vue";

const view = ref<"timeline" | "capacity">("timeline");
const selectedDate = ref(new Date());
// One breakpoint for the whole page, shared with the shell and the sheet gate:
// below md the phone timeline renders, at or above it the desktop one.
const { type } = useBreakpoints();
const mobileView = computed(() => type.value === "mobile");

// The page calls the timeline hook first, so the control row and whichever
// timeline is mounted share one selection (provideHook provides to the subtree
// from its first caller) and the selection survives a swap between the two.
const {
  activeResource,
  events,
  isWeekView,
  phoneColumns,
  resources,
  selectedResources,
  setSelectedResources
} = useEventCalendar();

// `enabled` is only ever true once the tenant has answered, so the row needs no
// separate resolved gate; MobileTimeline carries one for the card stack, the
// branch that must not mount before the answer.
const { enabled: mobileWebOptimization } = useResolvedFeatureFlag(
  "mobile_web_optimization"
);
const showControlRow = computed(
  () => mobileView.value && mobileWebOptimization.value
);
const isUnassignedActive = computed(
  () => activeResource.value?.id === UNASSIGNED_RESOURCE_ID
);
const activeAppointmentCount = computed(() =>
  activeResource.value
    ? appointmentCountFor(events.value, activeResource.value.id)
    : 0
);

const pickerOpen = ref(false);
const onControlRowSelect = () => {
  trackMobileWebEvent("schedule_technician_row_tap", {
    selection: isUnassignedActive.value ? "unassigned" : "technician"
  });
  pickerOpen.value = true;
};

// The chips come from the same store slice the desktop filter menu reads. It
// mounts on this page too (its desktop branch is CSS-hidden, not `v-if`) and
// dispatches the same fetch, which has no in-flight guard -- so asking again
// unconditionally would issue two identical requests per load.
const store = useStore();
const departments = computed(() => store.state.departments || []);
if (!store.state.departments?.length) store.dispatch("fetchDepartments");

const unassignedCount = computed(
  () => events.value.filter(isUnassignedAppointment).length
);

// How many schedules the board is showing at once, which is what decides
// whether a week is drawable and so whether the Day / Week toggle appears.
// The drawn columns rather than the raw selection: with nothing applied the
// grid still draws the board's own schedule, which the raw selection omits.
const technicianCount = computed(() => phoneColumns.value.length);

/**
 * Opened before anything has been applied, the sheet still marks the schedule
 * that is on screen — the behaviour the single-select picker had. Falling back
 * to an empty array instead would present the board's own technician as
 * unchosen, and "Clear" as a no-op.
 */
const sheetSelectedIds = computed(() => {
  const applied = selectedResources.value.map((resource) => resource.id);
  if (applied.length) return applied;
  return activeResource.value ? [activeResource.value.id] : [];
});

/**
 * The applied selection drives the grid: the composable turns it into one
 * column per schedule, keeps `?resource=` naming it when there is exactly one,
 * and remembers it in `?technician_ids=` and the browser so a reload or a new
 * tab comes back to the same columns.
 *
 * Unmounting the sheet here would cut the drawer's slide-out: the sheet emits
 * `apply` and then closes itself, and its `close` is what clears `pickerOpen`.
 */
const applySelection = (ids: number[]) => {
  setSelectedResources(ids);

  // What the grid actually draws, not the raw selection: an empty selection
  // still draws the board's own schedule, and that is a column.
  if (showControlRow.value) {
    trackMobileWebEvent("schedule_columns_shown", {
      count: phoneColumns.value.length
    });
  }
};

// While the banner shows, the column carries the control row's sticky offset
// as the banner's height (the same spacing token as its h-14), so the row pins
// beneath it and returns to the header once the banner is dismissed.
const bannerVisible = ref(false);

// The gear is the sheet's only entry point, so this tap is also the sheet's
// open signal; a second event on mount would double-count the same action.
const settingsOpen = ref(false);
const onSettingsTap = () => {
  trackMobileWebEvent("schedule_settings_tap");
  settingsOpen.value = true;
};

// The banner and the sheet are phone chrome, and the gear that opens the sheet
// unmounts above the breakpoint -- but an already-open sheet would not, and its
// "Use Old Scheduler" card writes whichever choice the CURRENT width selects.
// A sheet opened on a tablet in portrait and tapped after a rotation would turn
// the desktop choice off, under a card that says desktop is unchanged. So the
// phone chrome's state goes when the phone chrome does.
watch(showControlRow, (shown) => {
  if (shown) return;

  settingsOpen.value = false;
  bannerVisible.value = false;
});

const onReturnToToday = () => {
  trackMobileWebEvent("schedule_return_to_today");
};

/**
 * A week of several schedules has no single timeline to draw, which is why the
 * toggle hides at two. Hiding a control does not put the board back, though: a
 * selection that GROWS while Week is showing left the grid in week mode with
 * no way out of it, drawing seven columns per schedule side by side and
 * scrolling sideways, under a control row still reading "today".
 *
 * Written the way ScheduleViewToggle writes it -- query param first, then the
 * store -- so the URL cannot disagree with the board about which view is on.
 */
watch(technicianCount, (count) => {
  if (count <= 1 || !isWeekView.value) return;

  updateQueryParam("view", "day", true);
  store.dispatch("setScheduleBoardFilters", { view: "day" });
});

// Which unit the arrows moved, so week paging can be told apart from day paging
// without inferring it from the dates either side.
const onDateStepped = (unit: "day" | "week") => {
  trackMobileWebEvent("schedule_date_step", { unit });
};

// ScheduleViewToggle writes the view straight to the store rather than
// emitting, so the change itself is the only thing there is to measure. A watch
// also keeps the count honest: tapping the segment already showing is a no-op
// in the toggle and must not register as a view change here either.
watch(isWeekView, (week) => {
  trackMobileWebEvent("schedule_date_view_toggle", {
    view: week ? "week" : "day"
  });
});

const handleDateChange = (date: Date | number | string) => {
  selectedDate.value = moment(date).toDate();
  // The URL is the source of truth for the board date — sync it at nav time
  syncStartParam(selectedDate.value);
};

const handleViewChange = (_view: "timeline" | "capacity") => {
  view.value = _view;
};

onBeforeMount(() => {
  const { start } = getQueryVariables();
  if (start) {
    selectedDate.value = moment(start, "YYYY-MM-DD").hours(12).toDate();
  }
});
</script>
